/**
 * Shared formatting for provider interview-request slots.
 *
 * Used by both the authenticated chat event card
 * (NegotiationsPage/components/NegotiationEventCard.jsx) and the anonymous
 * guest negotiation page (GuestNegotiationPage.jsx) so slot rendering stays
 * identical across surfaces.
 *
 * Slot shape from the backend INTERVIEW_REQUEST_DETAILS event:
 *   { date: 'YYYY-MM-DD', startTime: 'HH:mm', endTime: 'HH:mm',
 *     timezone: 'America/...' }
 */

// Short labels for the four US timezones the interview form offers. Unknown
// IANA strings fall through to the raw value so legacy slots still render.
export const TZ_SHORT_LABELS = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
};

/** Convert "HH:mm" 24h to a "h:mm AM/PM" display string. */
export function formatTime12(time24) {
  if (!time24 || !String(time24).includes(':')) return time24 || '';
  const [h, m] = String(time24).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return time24;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Format one interview slot as e.g. "Tue, May 19 · 9:00 AM ET".
 * Parses the YYYY-MM-DD date in local time to avoid UTC-shift surprises.
 */
export function formatSlot(slot) {
  if (!slot) return '';
  const tz = TZ_SHORT_LABELS[slot.timezone] || slot.timezone || '';
  let dateStr = slot.date;
  try {
    // slot.date may arrive as 'YYYY-MM-DD' (from a negotiation event's data
    // payload) OR as a full ISO string like '2026-05-17T00:00:00.000Z'
    // (the raw Mongo Date field on swipe.interviewRequest.slots, used by
    // the payment modal). Take the leading YYYY-MM-DD before parsing, and
    // build the Date in local time so we never UTC-shift the day.
    const datePart = String(slot.date).slice(0, 10);
    const parts = datePart.split('-').map(Number);
    if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      dateStr = d.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }
  } catch (e) {
    /* fall back to the raw date string */
  }
  const time = formatTime12(slot.startTime);
  return `${dateStr} · ${time}${tz ? ` ${tz}` : ''}`;
}

/** Derive an "HH:mm" 30 minutes after the given "HH:mm" (interview length). */
function plus30(time24) {
  if (!time24 || !String(time24).includes(':')) return time24 || '';
  const [h, m] = String(time24).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return time24;
  const total = h * 60 + m + 30;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(
    total % 60,
  ).padStart(2, '0')}`;
}

/** Offset (minutes) of an IANA timezone at a given instant — DST aware. */
function tzOffsetMinutes(instant, timeZone) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = {};
    dtf.formatToParts(instant).forEach((p) => {
      parts[p.type] = p.value;
    });
    let hour = Number(parts.hour);
    if (hour === 24) hour = 0; // some engines emit "24" for midnight
    const asUTC = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      hour,
      Number(parts.minute),
      Number(parts.second),
    );
    return Math.round((asUTC - instant.getTime()) / 60000);
  } catch (e) {
    return 0;
  }
}

/**
 * Convert a wall-clock interview slot to absolute UTC Date instants.
 * Slot shape: { date:'YYYY-MM-DD'|ISO, startTime:'HH:mm', endTime?:'HH:mm',
 * timezone:'America/...' }. Returns { start, end } (Date) or null.
 */
export function slotToInstants(slot) {
  if (!slot || !slot.date || !slot.startTime) return null;
  const day = String(slot.date).slice(0, 10);
  const [y, mo, d] = day.split('-').map(Number);
  if ([y, mo, d].some((n) => Number.isNaN(n))) return null;
  const tz = slot.timezone || 'America/New_York';
  const endTime =
    slot.endTime && String(slot.endTime).includes(':')
      ? slot.endTime
      : plus30(slot.startTime);
  const toInstant = (timeStr) => {
    const [h, m] = String(timeStr).split(':').map(Number);
    // Treat the wall-clock as if UTC, then correct by the zone's offset. The
    // offset must be read at the slot's REAL instant, not at the naive one: for
    // a US zone the naive instant is 4 to 8 hours earlier than the real one, so
    // on a daylight saving change day it can sit on the other side of the 2:00
    // AM change and give the pre-change offset (an hour off for every slot
    // between 2:00 AM and the UTC time of the change). So take a first estimate
    // from the naive instant, then re-read the offset at that estimate. Same
    // two-pass arithmetic as the backend's slotWallClockInstant. A wall-clock
    // time that does not exist (spring forward gap) or happens twice (fall back
    // overlap) still resolves to one deterministic instant.
    const naive = Date.UTC(y, mo - 1, d, h || 0, m || 0, 0);
    const first = naive - tzOffsetMinutes(new Date(naive), tz) * 60000;
    return new Date(naive - tzOffsetMinutes(new Date(first), tz) * 60000);
  };
  return { start: toInstant(slot.startTime), end: toInstant(endTime) };
}

/**
 * Absolute start instant of a slot (Date), or null when the slot cannot be
 * read with confidence. Same arithmetic as the backend's slotEndInstant
 * (interview-ownership.ts): the stored date is the UTC-midnight calendar day,
 * and startTime is wall-clock in the slot's own IANA timezone. Never compare
 * slot.date alone: a 9:00 PM Pacific slot on the 5th starts on the 6th in UTC.
 *
 * A timezone the runtime cannot resolve returns null rather than silently
 * falling back to UTC: an unreadable slot is left alone, never disabled.
 */
export function slotStartInstant(slot) {
  if (!slot || !slot.date || !slot.startTime) return null;
  if (!/^\d{1,2}:\d{2}$/.test(String(slot.startTime))) return null;
  if (slot.timezone) {
    try {
      // eslint-disable-next-line no-new
      new Intl.DateTimeFormat('en-US', { timeZone: slot.timezone });
    } catch (e) {
      return null;
    }
  }
  const inst = slotToInstants(slot);
  if (!inst || Number.isNaN(inst.start.getTime())) return null;
  return inst.start;
}

/**
 * True when the slot starts at or before `now` (ms epoch or Date). This is the
 * same boundary the backend enforces with 409 slot_in_past: a slot whose start
 * equals now has already started. Unreadable slots are never reported past.
 */
export function isSlotPast(slot, now = Date.now()) {
  const start = slotStartInstant(slot);
  if (!start) return false;
  const nowMs = now instanceof Date ? now.getTime() : now;
  return start.getTime() <= nowMs;
}

/**
 * Stable identity for a slot (calendar day + start + timezone), independent of
 * its array index. Used to remember that the server rejected a slot as past
 * even when this device's clock still says it is in the future.
 */
export function slotKey(slot) {
  if (!slot) return '';
  return [
    String(slot.date || '').slice(0, 10),
    slot.startTime || '',
    slot.timezone || '',
  ].join('|');
}

/** Copy for a slot that can no longer be accepted. */
export const SLOT_PAST_LABEL = 'This time has passed.';

/**
 * Copy for a request whose every proposed time has passed. The reader is the
 * employer side by default (they message the provider). A provider reading a
 * facility-proposed request cannot replace the facility's times by messaging;
 * the control that works is their own "Suggest another time" button, which sits
 * on the same card.
 */
export const ALL_SLOTS_PAST_MESSAGE =
  'All proposed times have passed. Message the provider to set new times.';
export const ALL_SLOTS_PAST_MESSAGE_FOR_PROVIDER =
  'All proposed times have passed. Use Suggest another time to send new ones.';

/** Format a Date as a UTC "YYYYMMDDTHHMMSSZ" calendar stamp. */
function utcStamp(d) {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Wall-clock parts of an interview slot, formatted for calendar URLs:
 *   { day:'YYYYMMDD', start:'HHMMSS', end:'HHMMSS', tz:'America/...' }
 * These are the LOCAL times in the slot's own timezone — paired with a TZID
 * (.ics) or ctz (Google) param the event is pinned to that zone, so it shows
 * e.g. "1:30 PM Eastern" regardless of where the viewer is.
 */
function slotLocalParts(slot) {
  if (!slot || !slot.date || !slot.startTime) return null;
  const day = String(slot.date).slice(0, 10).replace(/-/g, '');
  if (day.length !== 8) return null;
  const endTime =
    slot.endTime && String(slot.endTime).includes(':')
      ? slot.endTime
      : plus30(slot.startTime);
  const hms = (t) => `${String(t).replace(':', '')}00`; // "13:30" -> "133000"
  return {
    day,
    start: hms(slot.startTime),
    end: hms(endTime),
    tz: slot.timezone || 'America/New_York',
  };
}

/**
 * Build a downloadable .ics calendar event for a confirmed interview slot.
 * Pins the event to the interview's own timezone via TZID, so it lands at the
 * agreed wall-clock time (e.g. 1:30 PM Eastern) in every calendar app.
 *
 * Optional `details` enriches the event to match the confirmation email's
 * invite:
 *   { descriptionText, attendees: [{ name, email }], organizer: { name, email },
 *     url }
 * Attendees + organizer land in the .ics only (importing an .ics never sends an
 * invite, and RSVP=FALSE suppresses RSVP prompts). When `details` is omitted the
 * output is the original minimal event, so existing callers are unaffected.
 */
export function buildInterviewIcs(slot, title = 'RxRooster Interview', details) {
  const p = slotLocalParts(slot);
  if (!p) return '';
  const esc = (s) =>
    String(s)
      .replace(/([,;\\])/g, '\\$1')
      .replace(/\n/g, '\\n');
  const uid = `rxrooster-interview-${p.day}-${p.start}-${Math.random()
    .toString(36)
    .slice(2, 8)}@rxrooster.com`;
  const description = details?.descriptionText || INTERVIEW_EVENT_DETAILS;
  const attendees = Array.isArray(details?.attendees)
    ? details.attendees.filter((a) => a && a.email)
    : [];
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//RxRooster//Interview//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${utcStamp(new Date())}`,
    `DTSTART;TZID=${p.tz}:${p.day}T${p.start}`,
    `DTEND;TZID=${p.tz}:${p.day}T${p.end}`,
    `SUMMARY:${esc(title)}`,
    `DESCRIPTION:${esc(description)}`,
  ];
  if (details?.url) lines.push(`URL:${details.url}`);
  if (details?.organizer?.email) {
    lines.push(
      `ORGANIZER;CN=${esc(details.organizer.name || 'RxRooster')}:mailto:${
        details.organizer.email
      }`,
    );
  }
  attendees.forEach((a) => {
    lines.push(
      `ATTENDEE;CN=${esc(
        a.name || a.email,
      )};ROLE=REQ-PARTICIPANT;RSVP=FALSE:mailto:${a.email}`,
    );
  });
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
}

/**
 * Trigger a browser download of the .ics for a confirmed interview slot.
 * On Apple devices the file opens directly in Calendar; elsewhere it imports
 * into any calendar app. No-op when the slot is incomplete.
 */
export function downloadInterviewIcs(slot, title, details) {
  const ics = buildInterviewIcs(slot, title, details);
  if (!ics) return;
  try {
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rxrooster-interview.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    /* download not supported — silent no-op */
  }
}

const INTERVIEW_EVENT_DETAILS = 'Interview scheduled via RxRooster.';

/**
 * Google Calendar event-create URL — opens Google Calendar in a new tab with
 * the interview pre-filled. `ctz` pins the event to the interview's own
 * timezone, so the wall-clock time matches what both parties agreed to.
 * Built manually so the `dates` separator stays a literal "/".
 */
export function buildGoogleCalendarUrl(
  slot,
  title = 'RxRooster Interview',
  details,
) {
  const p = slotLocalParts(slot);
  if (!p) return '';
  // Contact rides in `details` as text only. We deliberately do NOT pass an
  // `add=` attendee param, so saving the event never invites the counterparty
  // from the user's personal calendar.
  const detailText = details?.descriptionText || INTERVIEW_EVENT_DETAILS;
  const query = [
    'action=TEMPLATE',
    `text=${encodeURIComponent(title)}`,
    `dates=${p.day}T${p.start}/${p.day}T${p.end}`,
    `ctz=${encodeURIComponent(p.tz)}`,
    `details=${encodeURIComponent(detailText)}`,
  ].join('&');
  return `https://calendar.google.com/calendar/render?${query}`;
}

/**
 * Outlook event-create deeplink. `variant` selects the host:
 *   'office' → outlook.office.com (work / Microsoft 365)
 *   'live'   → outlook.live.com   (personal Outlook.com)
 * Built manually so the `path` value keeps its literal "/" separators.
 */
export function buildOutlookCalendarUrl(
  slot,
  title = 'RxRooster Interview',
  variant = 'office',
  details,
) {
  const inst = slotToInstants(slot);
  if (!inst) return '';
  const host = variant === 'live' ? 'outlook.live.com' : 'outlook.office.com';
  // Contact rides in the body as text only — no `to=` attendee param, so a
  // save never fires an invite from the user's personal calendar.
  const bodyText = details?.descriptionText || INTERVIEW_EVENT_DETAILS;
  const query = [
    'path=/calendar/action/compose',
    'rru=addevent',
    `subject=${encodeURIComponent(title)}`,
    `startdt=${encodeURIComponent(inst.start.toISOString())}`,
    `enddt=${encodeURIComponent(inst.end.toISOString())}`,
    `body=${encodeURIComponent(bodyText)}`,
  ].join('&');
  return `https://${host}/calendar/0/deeplink/compose?${query}`;
}
